/**
 * "Was this answer helpful?" — the control that sits on an AI output.
 *
 * On the artifact, never on the page. One screen can render fifty model outputs,
 * and a page-level feedback button cannot say which one was wrong. So this takes
 * the identity of the run it is attached to and files the feedback against it:
 * `componentId` is the execution's own id, which is what turns an opinion into
 * evidence a reviewer can reproduce.
 *
 * NOTHING IS WRITTEN UNTIL SUBMIT. A tap only opens the panel. The pattern this
 * replaces — save the thumb on click, then save the details — sounds friendlier
 * and is how the reference implementation ended up posting a dislike with none of
 * its required answers, taking a 400 and rolling back behind an open dialog. One
 * write, on submit, and Cancel leaves no row anywhere.
 *
 * The questions are NOT written here. They come from the definition that
 * resolves for this component type, exactly as the screen widget's do, so
 * changing what a dislike asks for is a data change on the AI Feedback screen.
 * The thumb is the definition's own SENTIMENT question, drawn as a bar; every
 * other question is rendered by the shared renderer underneath it.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'sharedUi/Button';
import { Dialog } from 'sharedUi/Dialog';
import { Message } from 'sharedUi/Message';
import { feedbackMasterService } from '../../services/feedback-master.service';
import { feedbackSubmissionService } from '../../services/feedback-submission.service';
import FeedbackFormRenderer from '../../pages/feedback/feedback-form-renderer';
import { isVisible, type AnswerMap } from '../../pages/feedback/question-visibility';
import { PERMISSIONS } from '../../constants/permissions';
import { extractErrorMessage } from '../../utils/error-handler';
import type { FeedbackMaster, FeedbackQuestion } from '../../types/feedback-master.types';

export interface AiFeedbackBarProps {
  /** Which kind of AI artifact this is: PROMPT_EXECUTION, RAG, EVAL, DOC_EXTRACTION… */
  componentType: string;
  /** What was configured — the prompt code, the RAG code, the document type. */
  componentCode?: string | null;
  /** The run actually seen. Without it feedback is an opinion; with it, evidence. */
  componentId?: string | null;
  /** The version that produced it — answers "did the fix work?" a release later. */
  componentVersion?: string | null;
  /** Declared context the definition may keep: model, latency, tokens, scores. */
  runtimeContext?: Record<string, unknown>;
  /** Chat surfaces only: which conversation, and which message inside it. */
  chatSessionId?: string | null;
  messageId?: string | null;
  /** The request this answer came from, for the log join. */
  traceId?: string | null;
  /** Shown at the right of the bar — model and latency usually. */
  meta?: React.ReactNode;
  className?: string;
  testId?: string;
}

const POSITIVE = 'POSITIVE';
const NEGATIVE = 'NEGATIVE';

/** The definition's own thumb question — by role, then by type, never by code. */
const sentimentQuestion = (definition?: FeedbackMaster): FeedbackQuestion | undefined =>
  (definition?.questions || []).find((q) => (q.signalRole || '').toUpperCase() === 'SENTIMENT')
  || (definition?.questions || []).find((q) => (q.questionType || '').toUpperCase() === 'SENTIMENT');

const isEmpty = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const AiFeedbackBar = ({
  componentType, componentCode, componentId, componentVersion,
  runtimeContext, chatSessionId, messageId, traceId,
  meta, className, testId = 'AiFeedbackBar',
}: AiFeedbackBarProps) => {
  const { pathname } = useLocation();
  const qc = useQueryClient();

  const permKeys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const canGive = useMemo(
    () => new Set(permKeys).has(PERMISSIONS.FEEDBACK_SUBMISSION_CREATE),
    [permKeys],
  );

  const [choice, setChoice] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitted, setSubmitted] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Most specific first, server-side: this project + this component, then the
  // project's default for the type, then the global default. A component nobody
  // has configured still resolves, which is why the bar works on a fresh install.
  const defQ = useQuery({
    queryKey: ['feedback-resolve', componentType, componentCode],
    queryFn: () => feedbackMasterService.resolve(componentType, undefined, componentCode || undefined),
    enabled: canGive && !!componentType,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const definition = defQ.data;
  const thumb = sentimentQuestion(definition);

  /** The form below the bar: every question except the thumb itself. */
  const panelDefinition = useMemo(() => {
    if (!definition) return undefined;
    return {
      ...definition,
      questions: (definition.questions || []).filter((q) => q.questionCode !== thumb?.questionCode),
    };
  }, [definition, thumb]);

  /**
   * Required means required IF IT WAS SHOWN — the same rule the renderer draws by
   * and the server validates by. Checking `required` alone would make a thumbs-up
   * fail on the questions only a thumbs-down asks.
   */
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    for (const q of definition?.questions || []) {
      if (!q.required || q.questionCode === thumb?.questionCode) continue;
      if (!isVisible(q, answers)) continue;
      if (isEmpty(answers[q.questionCode]?.value)) e[q.questionCode] = 'This one is required.';
    }
    return e;
  }, [definition, answers, thumb]);

  const answered = Object.entries(answers).filter(([, a]) => !isEmpty(a.value));

  const sendM = useMutation({
    mutationFn: () => feedbackSubmissionService.create({
      feedbackDefinitionId: definition!._id,
      answers: answered.map(([questionCode, a]) => ({
        questionCode,
        value: a.value,
        comment: a.comment?.trim() || null,
      })),
      component: {
        // The run, not the route: "which execution produced this" is the one
        // question a reviewer cannot answer from anything else on the record.
        componentId: componentId || traceId || null,
        componentVersion: componentVersion || null,
      },
      runtimeContext,
      sessionContext: {
        route: pathname,
        traceId: traceId || null,
        chatSessionId: chatSessionId || null,
        messageId: messageId || null,
      },
    }),
    onSuccess: () => {
      setSent(choice);
      setChoice(null);
      setAnswers({});
      setSubmitted(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ['feedback-submissions'] });
      qc.invalidateQueries({ queryKey: ['feedback-my-submissions'] });
    },
    // Visible, and the typing survives. A correction someone spent a minute
    // writing must never disappear into the console.
    onError: (e) => setError(extractErrorMessage(e, 'Could not send that — try again.')),
  });

  const pick = (value: string) => {
    setError(null);
    setSubmitted(false);
    if (choice === value) {           // tapping the same thumb closes the panel
      setChoice(null);
      setAnswers({});
      return;
    }
    setChoice(value);
    // The thumb IS an answer to the definition's sentiment question, which is why
    // a bare tap plus Send is already a complete submission.
    setAnswers(thumb ? { [thumb.questionCode]: { value } } : {});
  };

  /** Close with nothing written — the same reset the inline panel's Cancel did. */
  const cancel = () => {
    setChoice(null);
    setAnswers({});
    setError(null);
    setSubmitted(false);
  };

  const send = () => {
    setSubmitted(true);
    if (!definition || Object.keys(errors).length || !answered.length) return;
    sendM.mutate();
  };

  // No permission, or nothing resolves → no control. A thumb that opens a form
  // which cannot be submitted is worse than no thumb.
  if (!canGive || !definition || !thumb) return null;

  if (sent) {
    return (
      <div className={`flex items-center gap-2 text-xs text-ink-muted ${className || ''}`} data-testid={`${testId}.Sent`}>
        <i className={`pi ${sent === POSITIVE ? 'pi-thumbs-up-fill text-emerald-600' : 'pi-thumbs-down-fill text-rose-600'}`} />
        <span>Thanks — your feedback was sent.</span>
      </div>
    );
  }

  const thumbButton = (value: string, icon: string, label: string) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={choice === value}
      onClick={() => pick(value)}
      className={`inline-flex h-8 min-w-[2.75rem] items-center justify-center gap-1.5 rounded-lg border px-2.5 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
        choice === value
          ? value === POSITIVE
            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
          : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'}`}
      data-testid={`${testId}.${value}`}
    >
      <i className={`pi ${icon}`} />
      <span className="sr-only">{label}</span>
    </button>
  );

  return (
    <div className={`flex flex-col gap-2 ${className || ''}`} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
        <span>Was this helpful?</span>
        {thumbButton(POSITIVE, 'pi-thumbs-up', 'Helpful')}
        {thumbButton(NEGATIVE, 'pi-thumbs-down', 'Not helpful')}
        {meta ? <span className="ml-auto text-gray-400">{meta}</span> : null}
      </div>

      {/* The thumb stays on the artifact; the questions it opens are asked in a
          dialog. Still NOTHING IS WRITTEN UNTIL SEND — dismissing the dialog, by
          Cancel or by the mask, leaves no row anywhere, exactly as closing the
          inline panel did. The trade the dialog makes is that the answer being
          judged is covered while the correction is written; keep the dialog
          small enough that the artifact stays visible around it. */}
      <Dialog
        visible={!!choice && !!panelDefinition}
        onHide={cancel}
        header={choice === NEGATIVE ? 'Thanks — what went wrong?' : 'Glad it helped.'}
        style={{ width: 560, maxWidth: '95vw' }}
        modal
        dismissableMask
        data-testid={`${testId}.Panel`}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" size="small" severity="secondary" outlined
              onClick={cancel} disabled={sendM.isPending}
              data-testid={`${testId}.Cancel`} />
            <Button
              label={sendM.isPending ? 'Sending…' : 'Send feedback'}
              size="small"
              icon={sendM.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              disabled={sendM.isPending}
              onClick={send}
              data-testid={`${testId}.Send`}
            />
          </div>
        }
      >
        {panelDefinition && (
          <FeedbackFormRenderer
            definition={panelDefinition}
            answers={answers}
            onChange={setAnswers}
            submitted={submitted}
            errors={errors}
          />
        )}

        {error && <Message severity="error" text={error} className="mt-2 w-full" />}
      </Dialog>
    </div>
  );
};

export default AiFeedbackBar;
